// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.BorderSettings.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Border Settings component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toJS } from 'mobx';
import { BORDER_POSITION } from 'logic/data/Izenda.PropertiesBuilderConstants.js'
import Thinkness from 'share/components/controls/Izenda.Thinkness';
import ColorPicker from 'share/components/controls/Izenda.ColorPicker';
import DashStyle from 'share/components/controls/Izenda.DashStyle';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import localizer from 'share/localization/Izenda.Localizer';
import { get } from 'lodash/fp';

class BorderSettings extends Component {
  static propTypes = {
    disableMultipleBorderSettings: PropTypes.bool,
    onChange: PropTypes.func,
    borderStyle: PropTypes.object,
    previewComponent: PropTypes.func.isRequired,
    previewData: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.dashStyle = this.getInitialValue('dashStyle') || 'solid';
    this.color = this.getInitialValue('color') || '#000';
    this.thinkness = this.getInitialValue('thinkness') || 1;
    const borderSettings = toJS(props.value);

    // Border Style doesn't have structure then we shouldn't use mobx here.
    this.state = {
      borderStyle: borderSettings
    };
  }

  /**
   * @method getInitialValue
   * @description find the initial value for each border property based on what has already been set
   * @param {string} borderProp property being set
   */
  getInitialValue = (borderProp) => {
    const { value } = this.props;
    return (
      get(borderProp, value.top) ||
      get(borderProp, value.bottom) ||
      get(borderProp, value.left) ||
      get(borderProp, value.right) ||
      get(borderProp, value.midHor) ||
      get(borderProp, value.midVer)
    );
  }

  // Return data for update data.
  getValue = () => this.state.borderStyle;

  resetAllBorderStyle = () => {
    const currentStyle = this.state.borderStyle;
    for (let key in currentStyle) {
      if ({}.hasOwnProperty.call(currentStyle, key)) {
        currentStyle[key] = {};
      }
    }

    this.setState({
      borderStyle: currentStyle
    });
  };

  /**
   * Update border style
   * @param {Object} value
   * @param {boolean} isUpdate
   */
  setBorderStyle = (value: any, isUpdate: boolean) => {
    const nextStyle = this.state.borderStyle;
    const res = value.split(' ');

    res.forEach((item: any) => {
      const isNewStyle =
        nextStyle[item] &&
        nextStyle[item].dashStyle === this.dashStyle &&
        nextStyle[item].color === this.color &&
        nextStyle[item].thinkness === this.thinkness;

      if (!isNewStyle || isUpdate === true) {
        nextStyle[item] = {
          dashStyle: this.dashStyle,
          color: this.color,
          thinkness: this.thinkness
        };
      } else {
        nextStyle[item] = {};
      }
    });

    this.setState({ borderStyle: nextStyle });
  };

  /**
   * Update border style
   * @param {Object} value
   * @param {boolean} isUpdate
   */
  setBorderStyleAction = (value: any) => (isUpdate: boolean) => {
    this.setBorderStyle(value, isUpdate);
  };

  /**
   * Handle on change border style
   * @param {string} value
   */
  handleChangeBorderStyle = (value: string) => () => {
    switch (value) {
      case BORDER_POSITION.NONE:
        this.resetAllBorderStyle();
        this.setBorderStyle('');
        break;
      case BORDER_POSITION.OUTLINE:
        this.resetAllBorderStyle();
        this.setBorderStyle('top right left bottom', true);
        break;
      case BORDER_POSITION.INLINE:
        this.resetAllBorderStyle();
        this.setBorderStyle('midVer midHor', true);
        break;
      case BORDER_POSITION.ALL:
        this.setBorderStyle('top right left bottom midVer midHor', true);
    }
  };

  /**
   * Resolve border's styles
   * @param {string} value
   */
  getStylesBorder = (value: string) => {
    const borderStyle = this.state.borderStyle;
    switch (value) {
      case BORDER_POSITION.MID_VER:
        return {
          borderRightStyle: get('dashStyle', borderStyle.midVer),
          borderRightWidth: get('thinkness', borderStyle.midVer),
          borderRightColor: get('color', borderStyle.midVer)
        };
      case BORDER_POSITION.MID_HOR:
        return {
          borderBottomStyle: get('dashStyle', borderStyle.midHor),
          borderBottomWidth: get('thinkness', borderStyle.midHor),
          borderBottomColor: get('color', borderStyle.midHor)
        };
      case BORDER_POSITION.ALL:
        return {
          borderTopStyle: get('dashStyle', borderStyle.top),
          borderTopWidth: get('thinkness', borderStyle.top),
          borderTopColor: get('color', borderStyle.top),
          borderRightStyle: get('dashStyle', borderStyle.right),
          borderRightWidth: get('thinkness', borderStyle.right),
          borderRightColor: get('color', borderStyle.right),
          borderBottomStyle: get('dashStyle', borderStyle.bottom),
          borderBottomWidth: get('thinkness', borderStyle.bottom),
          borderBottomColor: get('color', borderStyle.bottom),
          borderLeftStyle: get('dashStyle', borderStyle.left),
          borderLeftWidth: get('thinkness', borderStyle.left),
          borderLeftColor: get('color', borderStyle.left)
        };
    }
  };

  render() {
    return (
      <GridLayout className='border-settings'>
        <GridLayout.Row className='u_noMarginAll border-settings-header'>
          <GridLayout.Col cols='6' className='text-center' xs='12'>
            {localizer.getText('COMMON_LABEL_CONFIGURATION')}
          </GridLayout.Col>
          <GridLayout.Col cols='6' className='text-center' xs='0'>
            {localizer.getText('COMMON_LABEL_PREVIEW')}
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row className='no-gutter u_noMarginAll border-settings-body'>
          {this.renderConfiguration()}
          {this.renderPreview()}
        </GridLayout.Row>
      </GridLayout>
    );
  }

  /**
   * On change dash style handler
   * @param {string} value
   */
  onChangeDashStyle = (value: string) => {
    this.dashStyle = value;
  };

  /**
   * On change color handler
   * @param {string} value
   */
  onChangeColor = (value: string) => {
    this.color = value;
  };

  /**
   * On change thickness handler
   * @param {string} value
   */
  onChangeThickness = ({ value }: { value: string }) => {
    this.thinkness = value;
  };

  /**
   * Render configuration
   */
  renderConfiguration = () => (
    <GridLayout.Col cols='6' className='text-center form-horizontal' xs='12'>
        <div className='form-group'>
          <label className='col-md-4 text-left u_paddingLeft'>{localizer.getText('BORDER_SETTING_LABEL_SETIING')}</label>
          <div className='col-md-8'>
            <a
              title={localizer.getText('BORDER_SETTING_LABEL_NONE')}
              className='icon-border'
              onClick={this.handleChangeBorderStyle('none')}
            >
              <i className='icon-no-boder' />
            </a>
            <a
              title={localizer.getText('BORDER_SETTING_LABEL_OUTLINE')}
              className='icon-border'
              onClick={this.handleChangeBorderStyle('outline')}
            >
              <i className='icon-all-boder' />
            </a>
            {
              !this.props.disableMultipleBorderSettings  &&
              <a
                title={localizer.getText('BORDER_SETTING_LABEL_INSIDE')}
                className='icon-border'
                onClick={this.handleChangeBorderStyle('inline')}
              >
                <i className='icon-table-noboder' />
              </a>
            }
            {
              !this.props.disableMultipleBorderSettings &&
              <a
                title={localizer.getText('COMMON_LABEL_ALL')}
                className='icon-border'
                onClick={this.handleChangeBorderStyle('all')}
              >
                <i className='icon-table-boder' />
              </a>
            }
          </div>
      </div>
      <div className='form-group'>
        <label className='col-md-4 text-left u_paddingLeft'>
          {localizer.getText('BORDER_SETTING_LABEL_DASH_STYLE')}
        </label>
        <div className='col-md-8'>
          <DashStyle dashStyle={this.dashStyle} onChange={this.onChangeDashStyle} className='u_marginRight' />
        </div>
      </div>
      <div className='form-group'>
        <label className='col-md-4 text-left u_paddingLeft'>{localizer.getText('COMMON_LABEL_COLOR')}</label>
        <div className='col-md-8 text-left'>
          <div className='borders-color'>
            <ColorPicker color={this.color} onChange={this.onChangeColor} />
          </div>
        </div>
      </div>

      <div className='form-group'>
        <label className='col-md-4 text-left u_paddingLeft'>
          {localizer.getText('BORDER_SETTING_LABEL_THICKNESS')}
        </label>
        <div className='col-md-8'>
          <Thinkness value={this.thinkness} onChange={this.onChangeThickness} />
        </div>
      </div>
    </GridLayout.Col>
  );

  /**
   * Render preview
   */
  renderPreview = () => (
    <GridLayout.Col cols='6' className='text-center' xs='0'>
      <span>{localizer.getText('BORDER_SETTING_TOOLTIP_APPLY_BORDER')}</span>

      <GridLayout>
        <GridLayout.Row className='border-settings-preview u_marginTop'>
          <GridLayout.Col cols='3' className='border-preview-right'>
            <a className='icon-border' onClick={this.setBorderStyleAction('top')}>
              <i className='icon-top-boder' />
            </a>
            {
              !this.props.disableMultipleBorderSettings &&
              <a className='icon-border' onClick={this.setBorderStyleAction('midHor')}>
                <i className='icon-mid-hor-boder' />
              </a>
            }
            <a className='icon-border' onClick={this.setBorderStyleAction('bottom')}>
              <i className='icon-bottom-boder' />
            </a>
          </GridLayout.Col>

          <GridLayout.Col cols='9' className='no-padding'>
            {
              <this.props.previewComponent
                getStylesBorder={this.getStylesBorder}
                previewData={this.props.previewData}
              />
            }
            <div className='border-preview-bottom'>
              <a className='icon-border' onClick={this.setBorderStyleAction('left')}>
                <i className='icon-left-boder' />
              </a>
              {
                !this.props.disableMultipleBorderSettings &&
                <a className='icon-border' onClick={this.setBorderStyleAction('midVer')}>
                  <i className='icon-mid-ver-boder' />
                </a>
              }
              <a className='icon-border' onClick={this.setBorderStyleAction('right')}>
                <i className='icon-right-boder' />
              </a>
            </div>
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    </GridLayout.Col>
  );
}
export default BorderSettings;



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.BorderSettings.js